import { Box, Flex, Text } from '@mantine/core'
import { Axios, ReactQuery } from 'cms-api'
import { type GetStaticProps, type NextPageWithLayout } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, type ReactNode } from 'react'
import { ROUTE } from 'shared-constants'
import { getPublicLayout } from '@public/components/PublicLayout'
import { Button } from 'components/Button'
import Icon from 'components/Icon/Icon'
import { FileUpload } from 'components/Search/FileUpload'
import { useUserAuth } from 'providers/userAuth'
import { prepareGetStaticNavigationProps } from 'routes/utils/prepareGetStaticNavigationProps'
import { Loader } from './Loader'
import { ResultsView } from './ResultsView'

interface ImageSearchPageProps {
  confidence: number
  vectorDistance: number
}

export const ImageSearch: NextPageWithLayout<ImageSearchPageProps> = ({
  confidence = 50,
  vectorDistance = 0.6,
}) => {
  const router = useRouter()
  const { isNoneSignedIn, isPageLoading: isUserLoading } = useUserAuth()

  useEffect(() => {
    if (!isUserLoading && isNoneSignedIn) {
      router.push(
        ROUTE.SIGN_IN({
          fromUrl: ROUTE.IMAGE_SEARCH(),
          returnUrl: ROUTE.IMAGE_SEARCH(),
        }),
        undefined
      )
    }
  }, [isNoneSignedIn, router, isUserLoading])

  const queryId =
    typeof window !== 'undefined'
      ? (router?.query?.id as string | undefined)
      : undefined

  const [imageSearchId, setImageSearchId] = React.useState<string | null>(null)

  const [isLoading, setIsLoading] = React.useState(false)

  useEffect(() => {
    if (queryId) {
      setImageSearchId(queryId)
    }
  }, [queryId])

  const { mutateAsync: mutatePostImageSearches } =
    ReactQuery.usePostImageSearches()

  const uploadImageSearchAttachment =
    ReactQuery.usePostImageSearchesUploadImage()

  const { data: imageSearchData, isLoading: isImageSearchLoading } =
    ReactQuery.useGetImageSearches(
      {
        filters: {
          documentId: imageSearchId ?? '',
        },
        fields: 'imageSearchStatus,error',
        populate: {
          labels: {
            fields: ['name', 'confidence'],
            populate: ['boundingBox', 'detectedProducts.product'],
          },
          image: {
            populate: {
              fields: 'url',
            },
          },
        } as any,
      },
      {
        query: {
          enabled: !!imageSearchId, // start when an id exists
          refetchInterval: (data) =>
            data?.data[0]?.imageSearchStatus === 'FINISHED' ? false : 2000,
        },
      }
    )

  const handleUploadImage = async (file: File) => {
    const result = await uploadImageSearchAttachment.mutateAsync({
      data: { files: [file] },
    })
    return result?.[0]
  }
  React.useEffect(() => {
    const state = imageSearchData?.data?.[0]?.imageSearchStatus
    if (state === 'FINISHED') {
      setIsLoading(false)
    }
  }, [imageSearchData])

  const handleCreateImageSearch = async (file: File) => {
    setIsLoading(true)
    const imageResponse = await handleUploadImage(file)

    if (imageResponse?.id) {
      const created = await mutatePostImageSearches({
        data: {
          data: {
            image: imageResponse.id,
            imageSearchStatus: 'QUEUED',
          },
        },
      })

      const id = created?.data?.documentId
      if (!id) {
        return
      }

      setImageSearchId(id)
      router.push(
        {
          pathname: ROUTE.IMAGE_SEARCH(),
          query: { id },
        },
        undefined,
        { shallow: true }
      )
    } else {
      setIsLoading(false)
    }
  }

  if (isLoading || isImageSearchLoading) {
    return <Loader />
  }

  const hasError = imageSearchData?.data[0]?.error
  if (hasError) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        style={{ minHeight: 400 }}
      >
        <Text fz={30} fw={500} mb={16}>
          Something went wrong
        </Text>
        <Text fz={16} c="#545554" ta="center" maw={400}>
          An error occurred while processing your image. Please try again later.
        </Text>
        <Button
          width="200px"
          mt={32}
          onClick={async () => {
            await router.replace(ROUTE.IMAGE_SEARCH(), undefined, {
              shallow: true,
            })
            window.location.reload()
          }}
        >
          Analyze image
        </Button>
      </Flex>
    )
  }

  return (
    <Box>
      {!imageSearchId && !isLoading && !queryId && (
        <Box
          w="100%"
          h="100%"
          style={{
            backgroundImage:
              'url("https://theexpert-production.b-cdn.net/image-search-bg.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            minHeight: 'calc(100vh - 80px)',
          }}
          pb={66}
          px={16}
        >
          <Text
            fz={{ base: '32px', lg: '50px' }}
            c="white"
            ff='"Lora", serif'
            ta="center"
            mb="xl"
            pt={64}
            lh="1.3"
          >
            Upload an image. <br />
            We’ll find the pieces you love.
          </Text>
          <Text c="white" ta="center" my="40px">
            Drag and drop your moodboard and inspiration to source matching
            décor.
          </Text>
          <FileUpload onFileSelect={handleCreateImageSearch} />
          <Flex justify="center" mt={40}>
            <Flex
              bg="piney-lake"
              p={16}
              justify="center"
              style={{ borderRadius: 10 }}
              direction={{ base: 'column', lg: 'row' }}
              align={{ base: 'center', lg: 'flex-start' }}
            >
              <Icon.AlertCircle />
              <Text c="white" ml={9} ta={{ base: 'center', lg: 'left' }}>
                <span
                  style={{
                    fontWeight: 500,
                    letterSpacing: '10%',
                    textTransform: 'uppercase',
                  }}
                >
                  Pro Tip:
                </span>{' '}
                Images featuring single items or well lit,
                <br /> high contrast rooms convert more accurate results.
              </Text>
            </Flex>
          </Flex>
        </Box>
      )}
      {imageSearchData && imageSearchData.data[0]?.labels?.length === 0 && (
        <Flex
          direction="column"
          align="center"
          justify="center"
          style={{ minHeight: 400 }}
        >
          <Text fz={30} fw={500} mb={16}>
            No matches found
          </Text>
          <Text fz={16} c="#545554" ta="center" maw={400}>
            We couldn’t find any products matching your image. Please try with a
            different image.
          </Text>
          <Button
            width="200px"
            mt={32}
            onClick={async () => {
              await router.replace(ROUTE.IMAGE_SEARCH(), undefined, {
                shallow: true,
              })
              window.location.reload()
            }}
          >
            Analyze image
          </Button>
        </Flex>
      )}

      {imageSearchData && imageSearchData.data[0]?.labels?.length > 0 && (
        <Box pos="relative" py={40}>
          <ResultsView
            results={imageSearchData.data[0]}
            confidence={confidence}
            vectorDistance={vectorDistance}
          />
        </Box>
      )}
    </Box>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const [baseProps, { data: imageSearchPage }] = await Promise.all([
    prepareGetStaticNavigationProps(),
    Axios.client().getImageSearchPage(),
  ])

  return {
    props: {
      ...baseProps.props,
      confidence: imageSearchPage?.confidence ?? 50,
      vectorDistance: imageSearchPage?.vectorDistance ?? 0.6,
    },
    revalidate: 60 * 60 * 24,
    notFound: baseProps.notFound,
  }
}

const PageLayout = (page: ReactNode) =>
  getPublicLayout(page, { isFullWidth: true, isWithBreadcrumbs: false })

ImageSearch.getLayout = PageLayout
